import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import Card from '../../components/common/Card';
import Pagination from '../../components/common/Pagination';
import EmptyState from '../../components/common/EmptyState';
import { getGuardianMessageLog } from '../../services/attendanceService';
import { MdArrowBack, MdSms } from 'react-icons/md';

const STATUS_LABEL = {
  sent:                'Sent',
  failed:              'Failed',
  skipped_no_phone:    'No guardian phone on file',
  skipped_optout:      'Family opted out',
  skipped_disabled:    'Messages were off',
  skipped_no_template: 'No message text configured',
  pending:             'Pending',
};
const STATUS_TINT = {
  sent: 'text-green-600', failed: 'text-red-500',
};

// What was sent, to whom, and why anything was skipped — the admin-visible counterpart to
// Stage 3's claim-before-send AttendanceNotification rows. Read-only.
export default function AttendanceMessages() {
  const [page, setPage] = useState(1);
  const { data, isPending } = useQuery({
    queryKey: ['guardian-message-log', page],
    queryFn: () => getGuardianMessageLog({ page, limit: 25 }).then(r => r.data),
  });

  const rows = data?.rows || [];

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div>
        <Link to="/admin/settings" className="inline-flex items-center gap-1 text-xs text-subtle hover:text-secondary mb-2">
          <MdArrowBack /> Back to Settings
        </Link>
        <h1 className="text-xl font-semibold text-secondary">Guardian Attendance Messages</h1>
        <p className="text-sm text-subtle mt-1">What was sent, to whom, and why anything was skipped.</p>
      </div>

      <Card>
        {isPending ? (
          <div className="h-32 bg-nav-hover animate-pulse rounded-xl" />
        ) : rows.length === 0 ? (
          <EmptyState icon={MdSms} title="No messages yet" description="Nothing has been sent since this was turned on." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-subtle border-b border-divider">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Student</th>
                  <th className="py-2 pr-3">Class</th>
                  <th className="py-2 pr-3">Guardian</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Message</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r._id} className="border-b border-divider last:border-0">
                    <td className="py-2 pr-3 text-muted whitespace-nowrap">{r.date}</td>
                    <td className="py-2 pr-3 text-secondary font-medium whitespace-nowrap">{r.studentName}</td>
                    <td className="py-2 pr-3 text-muted whitespace-nowrap">{r.className || '—'}</td>
                    <td className="py-2 pr-3 text-muted whitespace-nowrap">
                      {r.guardianName || '—'}{r.guardianPhone ? ` (${r.guardianPhone})` : ''}
                    </td>
                    <td className={`py-2 pr-3 whitespace-nowrap ${STATUS_TINT[r.status] || 'text-subtle'}`}>
                      {STATUS_LABEL[r.status] || r.status}
                      {r.status === 'failed' && r.errorMessage && (
                        <span className="block text-[11px] text-subtle">{r.errorMessage}</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-subtle max-w-xs truncate" title={r.messageText}>{r.messageText || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {data && data.pages > 1 && (
        <Pagination page={page} pages={data.pages} total={data.total} limit={25} onChange={setPage} />
      )}
    </div>
  );
}
